/**
 * Permission Partial Dialog
 * Handles cases where only one permission is granted
 */

import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import VideocamIcon from '@mui/icons-material/Videocam'
import MicIcon from '@mui/icons-material/Mic'

export const PermissionPartialDialog = ({
  open,
  onClose,
  onProceed,
  onRetry,
  grantedPermission, // 'camera' or 'microphone'
  deniedPermission, // 'camera' or 'microphone'
}) => {
  const getIcon = (permission) => {
    return permission === 'camera' ? (
      <VideocamIcon sx={{ fontSize: 40 }} />
    ) : (
      <MicIcon sx={{ fontSize: 40 }} />
    )
  }

  const getPermissionLabel = (permission) => {
    return permission === 'camera' ? 'Camera' : 'Microphone'
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="permission-partial-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 2,
        },
      }}
    >
      <DialogTitle id="permission-partial-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningIcon sx={{ fontSize: 32, color: '#ff9800' }} />
          <Typography variant="h6" component="div" fontWeight="bold">
            Partial Access Granted
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body1" color="text.secondary">
            You've granted access to your {getPermissionLabel(grantedPermission).toLowerCase()}, but
            not your {getPermissionLabel(deniedPermission).toLowerCase()}.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Box
              sx={{
                flex: 1,
                p: 2,
                border: '2px solid #4caf50',
                borderRadius: 2,
                textAlign: 'center',
                backgroundColor: '#f1f8f4',
              }}
            >
              {getIcon(grantedPermission)}
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 1, color: '#4caf50' }}>
                {getPermissionLabel(grantedPermission)} ✓
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Enabled
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                p: 2,
                border: '2px solid #f44336',
                borderRadius: 2,
                textAlign: 'center',
                backgroundColor: '#fef1f0',
              }}
            >
              {getIcon(deniedPermission)}
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 1, color: '#f44336' }}>
                {getPermissionLabel(deniedPermission)} ✗
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Blocked
              </Typography>
            </Box>
          </Box>

          <Alert severity="warning">
            For the best experience, we recommend enabling both camera and microphone. You can
            proceed with limited functionality or try enabling the{' '}
            {getPermissionLabel(deniedPermission).toLowerCase()} again.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 2, flexDirection: 'column' }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onRetry}
          sx={{
            backgroundColor: '#514387',
            '&:hover': {
              backgroundColor: '#403266',
            },
          }}
        >
          Enable {getPermissionLabel(deniedPermission)}
        </Button>
        <Button fullWidth variant="outlined" onClick={onProceed}>
          Proceed with {getPermissionLabel(grantedPermission)} Only
        </Button>
        <Button fullWidth variant="text" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PermissionPartialDialog


















